import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";

export default function Changelog() {
  const [markdownContent, setMarkdownContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchChangelog = async () => {
      try {
        const timestamp = new Date().getTime();
        const response = await fetch(`/CHANGELOG.md?v=${timestamp}`);
        if (!response.ok) throw new Error('Failed to fetch changelog');
        const text = await response.text();
        setMarkdownContent(text);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchChangelog();
  }, []);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-primary-400 mb-4" />
          <p className="text-slate-600 dark:text-slate-400">Loading changelog...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">Error Loading Changelog</h1>
          <p className="text-slate-600 dark:text-slate-400 mb-6">{error}</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 dark:hover:text-primary-400">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 md:py-24">
      {/* Header */}
      <div className="mb-10 flex items-center gap-5 sm:gap-8">
        <img
          src="/images/avatars/changelog_avatar.png"
          alt=""
          className="w-28 h-28 sm:w-36 sm:h-36 md:w-44 md:h-44 object-contain drop-shadow-lg shrink-0"
        />
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-3">
            Changelog
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Every update, enhancement, and fix across all releases.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8 md:p-10">
        {markdownContent ? (
          <div className="changelog-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({children}) => <h1 className="text-2xl md:text-3xl font-bold text-primary-500 dark:text-primary-400 text-center mb-6">{children}</h1>,
                h2: ({children}) => <h2 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-800 pb-2 mt-10 mb-4">{children}</h2>,
                h3: ({children}) => <h3 className="text-lg font-semibold text-primary-600 dark:text-primary-400 mt-6 mb-3">{children}</h3>,
                h4: ({children}) => <h4 className="text-base font-medium text-slate-800 dark:text-slate-200 mt-4 mb-2">{children}</h4>,
                p: ({children}) => <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-3">{children}</p>,
                ul: ({children}) => <ul className="space-y-1.5 mb-4 ml-4">{children}</ul>,
                li: ({children}) => (
                  <li className="flex items-start gap-2 text-sm">
                    <span className="text-primary-400 mt-1 shrink-0">&bull;</span>
                    <span className="text-slate-600 dark:text-slate-300">{children}</span>
                  </li>
                ),
                strong: ({children}) => <strong className="font-semibold text-slate-900 dark:text-white">{children}</strong>,
                em: ({children}) => <em className="italic text-slate-700 dark:text-slate-300">{children}</em>,
                code: ({children}) => <code className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-primary-600 dark:text-primary-400 rounded text-sm font-mono">{children}</code>,
                a: ({href, children}) => (
                  <a href={href} className="text-primary-500 hover:text-primary-600 dark:hover:text-primary-400 underline decoration-primary-400/30" target={href?.startsWith('http') ? '_blank' : undefined} rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}>
                    {children}
                  </a>
                ),
                hr: () => <hr className="my-8 border-slate-200 dark:border-slate-800" />,
              }}
            >
              {markdownContent}
            </ReactMarkdown>
          </div>
        ) : (
          <div className="text-slate-500 text-center py-8">Loading changelog content...</div>
        )}
      </div>

      {/* Back */}
      <div className="text-center mt-10">
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-400">
          Back to home
        </Link>
      </div>
    </div>
  );
}
